/* eslint-disable import/prefer-default-export */
import axios from '../../axios';

// ---------------------------------------------------------------------------
// RH / Recrutamento & Seleção — Entrevistas (service layer, handoff 0061).
//
// Agendar/listar ficam ANINHADAS ao candidato; ações sobre a entrevista usam
// `/hr/recruitment/interviews/:id`. Tradução camelCase → snake_case só aqui.
//
// `scheduled_at` DEVE ir em ISO-8601 COM offset (ex.: "2026-07-10T14:00:00-03:00")
// — a conversão do datetime-local pra esse formato é responsabilidade da página
// (utils/format.toIsoWithOffset). O backend rejeita `result` fora do subconjunto
// da rodada (422 invalid_result_for_round). Remarcar usa /reschedule (não
// /result com REMARCADO). Permissões: hr:candidate_interview:*.
// ---------------------------------------------------------------------------

export async function listCandidateInterviews(candidateId) {
   const response = await axios.get(
      `/hr/recruitment/candidates/${candidateId}/interviews`,
   );
   return response?.data?.data?.interviews ?? [];
}

export async function scheduleInterview(candidateId, payload = {}) {
   const { round, modality, interviewerUserId, scheduledAt } = payload;

   const body = {
      round,
      modality,
      scheduled_at: scheduledAt,
   };
   if (interviewerUserId) body.interviewer_user_id = interviewerUserId;

   const response = await axios.post(
      `/hr/recruitment/candidates/${candidateId}/interviews`,
      body,
   );
   return {
      interview: response?.data?.data?.interview ?? null,
      message: response?.data?.message ?? 'Entrevista agendada.',
   };
}

export async function getInterview(interviewId) {
   const response = await axios.get(
      `/hr/recruitment/interviews/${interviewId}`,
   );
   return response?.data?.data?.interview ?? null;
}

// Edição só antes do resultado (modalidade / entrevistador / horário).
export async function updateInterview(interviewId, patch = {}) {
   const { modality, interviewerUserId, scheduledAt } = patch;

   const body = {};
   if (modality) body.modality = modality;
   if ('interviewerUserId' in patch) {
      body.interviewer_user_id = interviewerUserId || null;
   }
   if (scheduledAt) body.scheduled_at = scheduledAt;

   const response = await axios.patch(
      `/hr/recruitment/interviews/${interviewId}`,
      body,
   );
   return {
      interview: response?.data?.data?.interview ?? null,
      message: response?.data?.message ?? 'Entrevista atualizada.',
   };
}

// Registrar resultado — muda o stage do candidato no backend. `result` precisa
// pertencer ao subconjunto da rodada; `structuredNotes` é objeto de texto.
export async function submitInterviewResult(interviewId, payload = {}) {
   const { attended, result, structuredNotes, notes } = payload;

   const body = { result };
   if (attended !== undefined) body.attended = attended;
   if (structuredNotes) body.structured_notes = structuredNotes;
   if (notes) body.notes = notes;

   const response = await axios.patch(
      `/hr/recruitment/interviews/${interviewId}/result`,
      body,
   );
   return {
      interview: response?.data?.data?.interview ?? null,
      candidate: response?.data?.data?.candidate ?? null,
      message: response?.data?.message ?? 'Resultado registrado.',
   };
}

// Remarcar — a atual vira REMARCADO (histórica) e nasce um registro novo (ativo)
// da mesma rodada. Retorna { previous, next }.
export async function rescheduleInterview(interviewId, payload = {}) {
   const { scheduledAt, modality, interviewerUserId } = payload;

   const body = { scheduled_at: scheduledAt };
   if (modality) body.modality = modality;
   if (interviewerUserId) body.interviewer_user_id = interviewerUserId;

   const response = await axios.post(
      `/hr/recruitment/interviews/${interviewId}/reschedule`,
      body,
   );
   const data = response?.data?.data ?? {};
   return {
      previous: data.previous ?? null,
      next: data.next ?? null,
      message: response?.data?.message ?? 'Entrevista remarcada.',
   };
}
